import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {DatePipe} from '@angular/common';
import {classToClass, plainToClass} from 'class-transformer';
import {Router} from '@angular/router';
import {forkJoin} from "rxjs";


import {GrowlService} from "@app/core";
import {CurrentTxnStatus} from "@app/shared/classes/current-txn-status";
import {AlphaNumDashRegex, EXCLUDE_PREFIX_COPY_PARTNER_ADDRESS, EXCLUDE_PREFIX_PARTNER_ADDRESS, LabelChangeAddressType, UsZipCodePattern, YearRange} from "@app/shared/classes/common";
import {UserSearch, UserSummary} from '@app/shared/classes/user';
import {OrgType, PartnerAddress, TaxNumber, TradingPartner} from '@app/shared/classes/trading-partner';
import {PdfTemplate} from '@app/shared/classes/pdf-template';

import {ApprovalStatusType} from '@app/shared/enums/approval-status-type.enum';
import {Format} from '@app/shared/enums/format.enum';
import {EnumType} from '@app/shared/enums/enum-type.enum';
import {CountryType} from '@app/shared/enums/ref-code-type.enum';
import {RefDomainType} from '@app/shared/enums/ref-domain-type.enum';
import {Transaction} from '@app/shared/enums/txn-type.enum';
import {RouteLink} from '@app/shared/enums/route-link.enum';
import {Validation} from '@app/shared/enums/validation.enum';
import {AddressType} from '@app/shared/enums/address-type.enum';

import {CommonDataService} from '@app/shared/services/common-data.service';
import {CommonTransformationService} from '@app/shared/services/common-transformation.service';
import {UtilityService} from '@app/shared/services/utility.service';
import {TradingPartnerService} from '@app/shared/services/trading-partner.service';
import {CountryCodeAlpha3} from "@app/shared/enums/country-code.enum";
import {RegExpWebsite} from "@app/shared/classes/reg-exp";
import {PhoneService} from "@app/shared/services/phone.service";
import {ActionStatusTypeEnum, ExternalContactType, GrowlType, PrimeNgConfig, PrimeNgSupportService, TableColumn} from "@app/shared";

/**
 * @author: Nishitha Salver
 * @author: Jin C refactored on Jan 2020
 * @desc: child component of TradingPartnerOrg. General Info to set up partner and their addresses
 * @createdOn: Jan 25th, 2018
 */

@Component({
  selector: 'app-org-general-info',
  templateUrl: './org-general-info.component.html',
  styleUrls: ['./org-general-info.component.css'],
  providers: [ConfirmationService]
})

export class OrgGeneralInfoComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() orgType: string;
  @Input() mainPartnerAddress: PartnerAddress;
  @Input() dbaPartnerAddress: PartnerAddress;
  @Input() partner: TradingPartner;

  @Output() handlerSave: EventEmitter<any> = new EventEmitter();
  @Output() handlerTab: EventEmitter<any> = new EventEmitter();

  // standard format and validation
  AddressType: typeof AddressType = AddressType;
  AlphaNumDashRegex = AlphaNumDashRegex;
  ApprovalStatusType: typeof ApprovalStatusType = ApprovalStatusType;
  CountryType = CountryCodeAlpha3;
  Format: typeof Format = Format;
  LabelChangeAddressType = LabelChangeAddressType;
  OrgType: typeof OrgType = OrgType;
  PrimeNgConfig = PrimeNgConfig;
  RegExpWebsite = RegExpWebsite;
  RouteLink: typeof RouteLink = RouteLink;
  TxnType: typeof Transaction = Transaction;
  UsZipCodePattern = UsZipCodePattern;
  Validation: typeof Validation = Validation;
  YearRange = YearRange;

  selectedSubmittedForId: number;
  showEditSSN: boolean = false;
  currentTxnStatus: CurrentTxnStatus;       // current status of trading partner to determine save, submit or resubmit

  selectedAddress: PartnerAddress;          // used for unique address name check
  addressToEdit: PartnerAddress;            // object modeled in address add/edit dialog - if new, has empty address.
                                            // if edited, it has a copy of the selected address.
  selectedTaxOptionValue: string;           // value set when either ssn or ein radio options selected
  vendorEmployee: UserSearch;               // if vendor is employee - then this objects is holds ess-user info
  viewOptions: SelectItem[] = [{label: "Yes", value: true}, {label: "No", value: false}];
  tableColumns: TableColumn[];              // column headers and fields to display on the address Table

  displayDialog: boolean = false;           // shows dialog on/off
  disableAddressName: boolean = true;       // address name field disabled
  userAutoCompleteOptions: UserSearch[];    // holds results of employee autocomplete search
  addrTypeDropDown: SelectItem[];           // a list of valid address types retrieved from backend for user selection e.g. MAIN,SHIP_TO,etc
  stateCodesDropdown: SelectItem[];         // a list of valid states (ref code) retrieved from backend for user selection
  countryCodesDropdown: SelectItem[];       // a list of valid country (ref code) retrieved from backend for user selection
  disabledIfBillToAddressType: boolean;

  errorMessageForSameAddressCombination: string;  // displays error message when address type, address name combination is repeated
  errorMessageForSameAddressType: string;         // displays error message when address type, address name combination is repeated

  showError: boolean;

  constructor(private confirmationService: ConfirmationService, private commonTransformationService: CommonTransformationService, private datePipe: DatePipe, private router: Router,
              private dataService: TradingPartnerService, private commonDataService: CommonDataService, private growl: GrowlService, private utilityService: UtilityService,
              private phoneService: PhoneService, private primeNgService: PrimeNgSupportService) {
  }

  /**
   * @desc on load of page, detect the booleans, pdfTemplateId and Submitted for to set the view
   */
  ngOnChanges() {
    if (this.partner) {
      if (this.partner.isSsn) {                   //sets radio button pre-selected on load of page
        this.selectedTaxOptionValue = TaxNumber.Ssn;
      } else {
        this.selectedTaxOptionValue = TaxNumber.Ein;
      }

      this.setDefaultCountry(this.mainPartnerAddress);
      this.setDefaultCountry(this.dbaPartnerAddress);
      this.setSubmittedForProperties();
      this.currentTxnStatus = new CurrentTxnStatus(this.partner.currentStatusName);

      this.handlerTab.emit(this.partner.governmentAgency);
    }
  }

  ngOnInit() {
    this.buildDropdown();              //gets state and country codes
  }

  ngAfterViewInit() {
    this.initTableColumns();
  }

  /**
   * @desc on load of page, trading partner specific pdf template is populated
   */
  onChangePdfTemplate(pdfTemplate: PdfTemplate): void {
    this.partner.pdfTemplateId = pdfTemplate.id;
  }

  onChangeEnableDBA(enableDBA: boolean): void {
    if (enableDBA) {  // in case input dbaPartnerAddress as not initialize with address type
      this.dbaPartnerAddress.addrType = AddressType.Dba;
      this.setDefaultCountry(this.dbaPartnerAddress);
    } else {  // nullify all the fields except id and version, otherwise it will throw unique index error when persist
      const dbaPartnerAddressId = this.dbaPartnerAddress.id;
      const dbaPartnerVersion = this.dbaPartnerAddress.version;
      this.utilityService.nullifyObjectContent(this.dbaPartnerAddress);
      this.dbaPartnerAddress.id = dbaPartnerAddressId;
      this.dbaPartnerAddress.version = dbaPartnerVersion;
      this.partner.dbaName = null;
    }
  }

  /**
   * @desc when isVendorEmployee? checked : then this searchable fields is shown to search against ess-users in db
   * @returns this.userAutoCompleteOptions is populated with list of results from backend.
   */
  searchUser(name: string): void {
    this.commonDataService.searchUserByName(name).subscribe(res => {
      this.userAutoCompleteOptions = plainToClass(UserSearch, res.list);
    });
  }

  /**
   * @desc an employee is selected as a vendor, set the business name to employee name and also set the main business address with employee address
   */
  onSelectUser(vendorEmployee: UserSearch): void {
    if (vendorEmployee) {
      this.partner.businessName = vendorEmployee.fullName;
      const locationId = vendorEmployee.locationId;
      // SSO-8435 - Removing specific properties of OrgAddress

      // TODO: Create a mapping service between OrgAddress and PartnerAddress and use it here
      this.dataService.findLocation(locationId).subscribe(res => {
        const addressId = this.mainPartnerAddress.id;
        Object.assign(this.mainPartnerAddress, plainToClass(PartnerAddress, res as PartnerAddress, {excludePrefixes: ['orgId', 'timesheetWorkLocationType']}));
        this.mainPartnerAddress.addrType = AddressType.Main;
        this.mainPartnerAddress.id = addressId;               // restore address id to avoid error persisting
      });
    }
  }

  /**
   * @desc populate DBA address with the main address
   */
  onClickCopyMainToDbaAddress(tradingPartner: TradingPartner): void {
    if (tradingPartner.enableDBA) {
      tradingPartner.dbaName = this.partner.businessName;
      const dbaPartnerAddressId = this.dbaPartnerAddress.id;
      const dbaPartnerVersion = this.dbaPartnerAddress.version;
      Object.assign(this.dbaPartnerAddress, this.mainPartnerAddress); // use object assign instead of classToClass to keep the obj address
      this.dbaPartnerAddress.id = dbaPartnerAddressId;                // restore address id in order to avoid persistence error
      this.dbaPartnerAddress.version = dbaPartnerVersion;
      this.dbaPartnerAddress.addrType = AddressType.Dba;
    }
  }

  /**
   * @desc when SSN radio button clicked, enables ssn mask
   */
  onSelectSSN(): void {
    this.selectedTaxOptionValue = TaxNumber.Ssn;
    this.partner.isSsn = true;
  }

  /**
   * @desc when EIN radio button clicked, enables ein mask
   * @returns this.enableEINField is set to true and show ein mask input field
   */
  onSelectEIN(): void {
    this.selectedTaxOptionValue = TaxNumber.Ein;
    this.partner.isSsn = false;
  }

  closeDialog(): void {
    this.displayDialog = false;
  }

  /**
   * @desc if unchecked, reset mainPartner address, business name and vendorEmployee
   */
  isVendorYourEmployee(isEmployee: boolean): void {
    if (!isEmployee) {
      this.vendorEmployee = null;
      this.partner.businessName = null;
      this.utilityService.nullifyObjectContent(this.mainPartnerAddress);
    }
  }

  /**
   * @desc opens dialog to add new address info. by default country is set to USA
   */
  onClickAddAddressDialog(): void {
    this.errorMessageForSameAddressType = null;
    this.selectedAddress = null;
    this.addressToEdit = new PartnerAddress();
    this.addressToEdit.country = CountryType.Usa; // default to US address
    this.setDisabledIfAddressTypeIsBillTo();
    this.displayDialog = true;
  }

  /**
   * @desc this.newAddress with user input info is copied into temporary object and is pushed to this.tradingPartner.partnerAddresses.
   *       emit of string is sent as a trigger parent component to Save.
   * @returns this.tradingPartner.partnerAddresses is updated here and also in parent component(OrgInfoComponent)
   */
  onClickAddAddress(partnerAddress: PartnerAddress): void {
    this.partner.partnerAddresses = this.primeNgService.unshift(this.partner.partnerAddresses, partnerAddress);
    this.displayDialog = false;
    this.handlerSave.emit(ActionStatusTypeEnum.AddressAdded);
  }

  /**
   * @desc opens dialog to edit address info. Copy of this.selectedAddress made into this.newAddress.
   */
  onClickEditAddressDialog(partnerAddress: PartnerAddress): void {
    this.selectedAddress = partnerAddress;
    this.addressToEdit = classToClass(partnerAddress);
    if (this.addressToEdit.addrType) {
      this.disableAddressName = false;
    }
    this.setDisabledIfAddressTypeIsBillTo();
    this.displayDialog = true;
  }

  /**
   * @desc update the partner.partnerAddress since partnerAddress is updated
   *       handlerSave is called to save the partner address by updating the entire partner record
   */
  onClickUpdateAddress(partnerAddress: PartnerAddress, partner: TradingPartner): void {
    if (partner?.partnerAddresses) {
      partner.partnerAddresses = this.primeNgService.replaceById(partner.partnerAddresses, partnerAddress);
      this.displayDialog = false;
      this.handlerSave.emit(ActionStatusTypeEnum.AddressUpdated);
    } else {  /// should never get this error
      console.error("tradingPartner or its partner address array is empty");
    }
  }

  /**
   * @desc triggers a confirmation dialog, whether to delete. once confirmed, delete the partner address from partner.partnerAddresses
   */
  onClickDeleteAddress(partnerAddress: PartnerAddress): void {
    this.confirmationService.confirm({
      message: `Are you sure that you want to delete this Address (${partnerAddress.addrName})?`,
      header: 'Delete Address Confirmation',
      accept: () => {
        this.partner.partnerAddresses = this.primeNgService.deleteById(this.partner.partnerAddresses, partnerAddress.id);
        if (partnerAddress.addrType == AddressType.BillTo) {
          this.partner.billingAddressSameAsMainAddress = false;
        }
        this.handlerSave.emit(ActionStatusTypeEnum.AddressDeleted);
      },
      reject: () => {
      }
    });
  }

  enableTaxFields(checked: boolean): void {
    if (checked) {
      this.handlerTab.emit(true);
    } else {
      this.handlerTab.emit(false);
    }
  }

  /**
   * @desc handler for submitted-for common component
   */
  loadDelegate(user: UserSummary): void {
    this.partner.submittedForId = user == null ? null : user.id;
  }

  /**
   * @desc in add address dialog, when address type dropdown is selected, then address name is input field is enabled by setting this.disableAddressName to false else true
   */
  onChangeAddressType(value: boolean): void {
    this.disableAddressName = !value;

    const billToAddressIndex = this.partner.partnerAddresses.findIndex(address => address.addrType == AddressType.BillTo);
    if ((billToAddressIndex > -1 && this.addressToEdit.id != this.partner.partnerAddresses[billToAddressIndex].id && this.addressToEdit.addrType == AddressType.BillTo)) {
      this.errorMessageForSameAddressType = "Billing contact type already exists";
    } else {
      this.errorMessageForSameAddressType = null;
    }
    this.setDisabledIfAddressTypeIsBillTo();
  }

  /**
   * @desc checks uniqueness of the address name & address type combination. this will in turn show or hide "Add" button in the dialog.
   * @returns this.errorMessageForSameAddressCombination is set based on true if unique; false if the combination already exists. returns boolean
   */
  onBlurCheckAddressName(addressToEdit: PartnerAddress): void {
    if (this.partner.id) {
      if (!(addressToEdit.id && this.selectedAddress?.addrName === addressToEdit.addrName)) {
        this.dataService.checkAddressNameUniqueness(this.partner.id, addressToEdit.addrType, addressToEdit.addrName).subscribe(res => {
          if (res["IfUniquePartnerAddress"] == true) {
            this.errorMessageForSameAddressCombination = null;
          } else {
            this.errorMessageForSameAddressCombination = 'This Address Name has already been used for this Address Type. Please give a new  Address Name';
          }
        });
      }
    }
  }

  /**
   * @desc event trigger to parent that info is entered and is now ready to execute update tradingPartner
   */
  onClickSavePartner(partner: TradingPartner): void {
    if (!partner.id) {
      this.handlerSave.emit(ActionStatusTypeEnum.Save);
    } else {
      this.handlerSave.emit(ActionStatusTypeEnum.Update);
    }
  }

  onClickEditPartner(): void {
    this.confirmationService.confirm({
      message: `Click "Yes" to confirm you wish to edit the ${this.orgType.toLowerCase()} information. Changes to this information will require you to resubmit the ${this.orgType.toLowerCase()} for approval.`,
      header: 'Edit Information',
      accept: () => {
        this.handlerSave.emit(ActionStatusTypeEnum.Edit);
      },
      reject: () => {
      }
    });
  }

  /**
   * @desc: trigger to parent that info is entered and is now ready to submit tradingPartner
   */
  onClickSubmitPartnerForApproval(): void {
    this.handlerSave.emit(ActionStatusTypeEnum.Submit);
  }

  /**
   * @desc: trigger to show error message "need to set up tax for this SHIP_TO address"
   */
  showAddressError(): void {
    this.showError = true;
  }

  /**
   * @desc add billing address if it is same as main address
   */
  onChangeAddBillingAddressIfSameAsMain(): void {
    if (this.partner.billingAddressSameAsMainAddress && this.mainPartnerAddress) {
      const billToAddressIndex = this.partner.partnerAddresses.findIndex(address => address.addrType == AddressType.BillTo);
      if (billToAddressIndex > -1) {
        this.confirmationService.confirm({
          message: `Are you sure you want to override the existing billing address ?`,
          header: 'Update billing address',
          accept: () => {
            this.onChangeUpdateBillingAddress();
          },
          reject: () => {
            this.partner.billingAddressSameAsMainAddress = false;
          }
        });
      } else {
        this.confirmationService.confirm({
          message: `Are you sure you want to add billing address same as main address?`,
          header: 'Add billing address',
          accept: () => {
            this.addressToEdit = classToClass(this.mainPartnerAddress, {excludePrefixes: EXCLUDE_PREFIX_COPY_PARTNER_ADDRESS});
            this.addressToEdit.addrType = AddressType.BillTo;
            this.addressToEdit.addrName = ExternalContactType.Billing;
            this.onClickAddAddress(this.addressToEdit);
          },
          reject: () => {
          }
        });
      }
    } else {
      this.growl.add(GrowlType.Warn, "Please update the main address first");
    }
  }

  /**
   * update the Bill-To address if billingAddressSameAsMainAddress is true
   */
  onChangeUpdateBillingAddress(): void {
    if (this.partner.billingAddressSameAsMainAddress) {
      const index = this.partner.partnerAddresses.findIndex(row => row.addrType === AddressType.BillTo);
      if (index > -1) {
        this.partner.partnerAddresses[index] = classToClass(this.mainPartnerAddress, {excludePrefixes: EXCLUDE_PREFIX_PARTNER_ADDRESS});
      }
    }
  }

  private setDisabledIfAddressTypeIsBillTo(): void {
    this.disabledIfBillToAddressType = this.partner?.billingAddressSameAsMainAddress && this.addressToEdit?.id && this.addressToEdit?.addrType == AddressType.BillTo;
  }

  /**
   * @desc build dropdown menus
   * @returns this.stateCodesDropdown - state codes dropdown menu retrieved from backend
   *          this.countryCodesDropdown -  country codes dropdown menu retrieved from backend
   *          this.addrTypeDropDown - address type dropdown menu retrieved from backend, main and dba are spliced as they cant be repeated
   */
  private buildDropdown(): void {
    forkJoin(
      this.commonDataService.getRefCodeAsDropdown(RefDomainType.State, true),
      this.commonDataService.getRefCodeAsDropdown(RefDomainType.Country, true),
      this.commonDataService.getEnumPairAsDropdown(EnumType.AddressType, null, null, null, null, LabelChangeAddressType),
    ).subscribe(([stateCodesDropdown, countryCodesDropdown, addrTypeDropDown]) => {
      this.stateCodesDropdown = stateCodesDropdown;
      this.countryCodesDropdown = countryCodesDropdown;
      this.addrTypeDropDown = addrTypeDropDown.filter(i => (i.value != AddressType.Main || i.value != AddressType.Dba));
    });
  }

  private initTableColumns(): void {
    this.tableColumns = [
      {field: 'addrType', header: 'Address Type'},
      {field: 'addrName', header: 'Address Name'},
      {field: 'addrLn1', header: 'Address'},
      {field: 'city', header: 'City'},
      {field: 'stateCode', header: 'State'},
      {field: 'postalCode', header: 'Postal Code'},
      {field: 'country', header: 'Country'},
      {field: 'mainPhone', header: 'Main Phone'},
      {field: 'faxPhone', header: 'Fax Phone'},
      {field: 'action', header: 'Actions'}
    ];
  }

  private setSubmittedForProperties(): void {
    this.selectedSubmittedForId = this.partner?.submittedForId;
  }

  private setDefaultCountry(partnerAddress: PartnerAddress): void {
    if (!partnerAddress?.id) {
      partnerAddress.country = CountryType.Usa;
    }
  }

}
